import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Kinetic";
import { SITE, SERVICES, CONTACT_DIRECTORY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: SERVICES[0].title,
    trip_date: "",
    trip_time: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received — our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", service: SERVICES[0].title, trip_date: "", trip_time: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call dispatch instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="page-contact" className="pt-20">
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12 lg:py-24">
        <Reveal>
          <Eyebrow>Contact & Dispatch</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-semibold leading-[1.05] text-ivory sm:text-6xl">
            Let&apos;s plan your <em className="gold-text italic">arrival.</em>
          </h1>
        </Reveal>

        <div className="mt-16 grid gap-14 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="space-y-8">
              {SITE.phone && (
                <div className="flex items-start gap-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                      Dispatch · 24/7
                    </p>
                    <a
                      href={SITE.phoneHref}
                      data-testid="contact-phone-link"
                      className="mt-1 block font-display text-2xl text-ivory transition-colors hover:text-gold"
                    >
                      {SITE.phone}
                    </a>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                  <Mail className="h-5 w-5" />
                </span>
                <div className="w-full">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                    Email a department
                  </p>
                  <ul className="mt-3 space-y-3">
                    {CONTACT_DIRECTORY.map((d) => (
                      <li key={d.email} className="flex flex-col">
                        <span className="text-sm text-ivory/55">{d.dept}</span>
                        <a
                          href={`mailto:${d.email}`}
                          data-testid={`contact-email-${d.email.split("@")[0]}`}
                          className="mt-0.5 pl-4 text-sm text-gold transition-colors hover:text-gold-light"
                        >
                          {d.email}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                    Service Area
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-ivory/70">
                    {SITE.area}
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-gold/15 bg-obsidian-800 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                  Reservations
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ivory/70">
                  The fastest way to book is our online reservation system —
                  live quotes, instant confirmation, free cancellation window.
                </p>
                <Link
                  to="/reservation"
                  data-testid="contact-reservation-link"
                  className="mt-4 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-gold underline-offset-4 hover:underline"
                >
                  Go to reservations →
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="gold-frame rounded-2xl bg-obsidian-800 p-8 sm:p-10"
              data-testid="contact-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Full name *
                  </label>
                  <input
                    id="cf-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className="field"
                    placeholder="Your name"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Email *
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className="field"
                    placeholder="you@example.com"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Phone
                  </label>
                  <input
                    id="cf-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    className="field"
                    placeholder="+1 …"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label htmlFor="cf-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Service
                  </label>
                  <select
                    id="cf-service"
                    value={form.service}
                    onChange={set("service")}
                    className="field"
                    data-testid="contact-select-service"
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-date" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Trip date
                  </label>
                  <input
                    id="cf-date"
                    type="date"
                    value={form.trip_date}
                    onChange={set("trip_date")}
                    className="field [color-scheme:dark]"
                    data-testid="contact-input-trip-date"
                  />
                </div>
                <div>
                  <label htmlFor="cf-time" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Pickup time
                  </label>
                  <input
                    id="cf-time"
                    type="time"
                    value={form.trip_time}
                    onChange={set("trip_time")}
                    className="field [color-scheme:dark]"
                    data-testid="contact-input-trip-time"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/60">
                    Message *
                  </label>
                  <textarea
                    id="cf-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className="field resize-none"
                    placeholder="Tell us about your trip — dates, pickup, destination, vehicle preference…"
                    data-testid="contact-input-message"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian-900 transition-all duration-300 hover:shadow-[0_0_40px_rgba(217,164,65,0.4)] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send message"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
